/**
 * SSE 실시간 연결을 디바이스 단위로 구분하기 위한 클라이언트 식별 유틸.
 *
 * - 일반 웹은 브라우저 프로필마다 안정적인 `agentteams.web.deviceId`를 localStorage에 보관한다.
 * - 데스크탑 앱은 기존 `agentteams.desktop.deviceId`(desktopBridge)를 그대로 사용한다.
 * - 이 값은 서버의 SSE 연결 제한/진단 용도로만 쓰며, 보안·과금 판단에는 사용하지 않는다.
 */

import { getOrCreateDesktopDeviceId, hasDesktopBridge } from './desktopBridge';

export const WEB_DEVICE_ID_KEY = 'agentteams.web.deviceId';

export type RealtimeClientKind = 'WEB' | 'DESKTOP';

export type RealtimeClientIdentity = {
  deviceId: string;
  clientKind: RealtimeClientKind;
};

const createDeviceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// localStorage 접근이 막힌 환경(사생활 모드 등)에서도 같은 탭 세션 동안은 동일한 fallback id를
// 쓰도록 모듈 수준에 캐시한다. 매번 새 id를 주면 디바이스 제한이 재연결마다 누적되기 때문이다.
let cachedFallbackDeviceId: string | null = null;

export function getOrCreateWebDeviceId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.localStorage.getItem(WEB_DEVICE_ID_KEY);
    if (existing) return existing;
    const next = createDeviceId();
    window.localStorage.setItem(WEB_DEVICE_ID_KEY, next);
    return next;
  } catch {
    // 진단 가능하도록 `web-fallback-` 접두어를 붙여 localStorage 미사용 연결임을 드러낸다.
    if (!cachedFallbackDeviceId) {
      cachedFallbackDeviceId = `web-fallback-${createDeviceId()}`;
    }
    return cachedFallbackDeviceId;
  }
}

/**
 * 현재 실행 환경의 안정적인 `deviceId`와 `clientKind`를 반환한다. 데스크탑 브릿지가 있으면
 * 데스크탑 식별자를, 아니면 일반 웹 식별자를 사용한다.
 */
export function getRealtimeClientIdentity(): RealtimeClientIdentity {
  if (hasDesktopBridge()) {
    return { deviceId: getOrCreateDesktopDeviceId(), clientKind: 'DESKTOP' };
  }
  return { deviceId: getOrCreateWebDeviceId(), clientKind: 'WEB' };
}
